import { NSARouterMakerResult } from "namirasoft-account-react";
import { InferencerType, RouterDefaultInferencerType, RouterFullRow, RouterInputRow, RouterRouteInputRow, RouterRow } from "namirasoft-inference";
import { NSBox, NSBoxString, NSBoxTextArea, NSColumn, NSRepeater, NSRow, NSSpace, NSSpaceSizeType } from "namirasoft-site-react";
import { Component, createRef } from "react";
import InferencerCommonFields from "../components/InferencerCommonFields";
import InferencerGraph from "../components/InferencerGraph";
import InferencerLoader from "../components/InferencerLoader";
import InferencerTypeID from "../components/InferencerTypeID";
import InferencerVisualizer from "../components/InferencerVisualizer";
import RouterRouteLine from "../components/RouterRouteLine";
import { InferenceEntities } from "../InferenceEntities";
import { InferenceInfo } from "../InferenceInfo";
import { InferenceLayout } from '../InferenceLayout';
import { InferenceRouterProps } from "../InferenceRouterProps";

interface RouterEditPageProps extends NSARouterMakerResult, InferenceRouterProps
{
    isEdit: boolean;
}

export default class RouterEditPage extends Component<RouterEditPageProps>
{
    NSRepeater_Routes = createRef<NSRepeater<RouterRouteLine, RouterRouteInputRow>>();
    NSBoxString_Name = createRef<NSBoxString>();
    InferencerTypeID_Default = createRef<InferencerTypeID<RouterDefaultInferencerType>>();
    InferencerCommonFields_Ids = createRef<InferencerCommonFields>();
    NSBoxTextArea_Description = createRef<NSBoxTextArea>();

    override render()
    {
        return (
            <InferenceLayout<RouterRow, RouterInputRow>
                {...this.props}
                action={{ description: this.props.isEdit ? InferenceInfo.router.edit : InferenceInfo.router.create }}
                pages={{
                    entity: InferenceEntities.router(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_edit: {
                                editor: {
                                    getInputRow: (): RouterInputRow =>
                                    {
                                        let name = this.NSBoxString_Name.current?.getValue() ?? "";
                                        let defaultInferencer = this.InferencerTypeID_Default.current?.getValue() ?? { inferencer_type: RouterDefaultInferencerType.MODEL, inferencer_id: "" };
                                        let default_inferencer_type = defaultInferencer.inferencer_type;
                                        let default_inferencer_id = defaultInferencer.inferencer_id;
                                        let { validator_id, budget_id, memory_id, wiper_id, log_group_id } = this.InferencerCommonFields_Ids.current?.getValue() ?? InferencerCommonFields.EMPTY;
                                        let description = this.NSBoxTextArea_Description.current?.getValue() ?? null;
                                        let routes = this.NSRepeater_Routes.current?.getValue() ?? [];

                                        return {
                                            workspace_id: null,
                                            name,
                                            default_inferencer_type,
                                            default_inferencer_id,
                                            validator_id,
                                            budget_id,
                                            memory_id,
                                            wiper_id,
                                            log_group_id,
                                            description,
                                            routes,
                                            router_category: [],
                                            router_field: [],
                                            router_tag: [],
                                        };
                                    },
                                    setRow: (row: RouterRow) =>
                                    {
                                        let full = row as RouterFullRow;

                                        this.NSBoxString_Name.current?.setValue(full.name);
                                        this.InferencerTypeID_Default.current?.setValue({ inferencer_type: full.default_inferencer_type, inferencer_id: full.default_inferencer_id });
                                        this.InferencerCommonFields_Ids.current?.setValue(full);
                                        this.NSBoxTextArea_Description.current?.setValue(full.description);
                                        this.NSRepeater_Routes.current?.setValue((full.routes ?? []).map(r => ({
                                            order: r.order,
                                            ccg_type: r.ccg_type,
                                            ccg_id: r.ccg_id,
                                            ccg_not: r.ccg_not,
                                            inferencer_type: r.inferencer_type,
                                            inferencer_id: r.inferencer_id,
                                        })));
                                    }
                                },
                                ui: {
                                    extra_tabs: [
                                        {
                                            title: "Routes",
                                            clicked: true,
                                            getContent: () => (
                                                <NSRepeater<RouterRouteLine, RouterRouteInputRow>
                                                    ref={this.NSRepeater_Routes}
                                                    title="Routes"
                                                    required
                                                    min={1}
                                                    createItem={(gIndex: number, lIndex: number, ref: React.RefObject<RouterRouteLine>, onChange: () => void, onDelete: undefined | ((index: number) => void)) =>
                                                    {
                                                        return <RouterRouteLine
                                                            ref={ref}
                                                            {...this.props}
                                                            key={`RouterRouteLine_${gIndex}`}
                                                            index={lIndex}
                                                            deletable={true}
                                                            onChanged={() => { onChange(); }}
                                                            onDelete={() => { onDelete?.(lIndex); }}
                                                        />;
                                                    }}
                                                    isEmpty={function (item: RouterRouteLine, _: RouterRouteInputRow | null): boolean
                                                    {
                                                        return !item.isDirty();
                                                    }}
                                                    getValue={function (item: RouterRouteLine, checkError: boolean): RouterRouteInputRow | null
                                                    {
                                                        return item.getValue(checkError);
                                                    }}
                                                    setValue={function (item: RouterRouteLine, _: number, value: RouterRouteInputRow | null): void
                                                    {
                                                        if (value)
                                                            item.setValue(value);
                                                    }}
                                                    style={{ width: NSBox.width.triple }}
                                                />
                                            )
                                        },
                                        {
                                            title: "Common",
                                            clicked: true,
                                            getContent: () => (
                                                <InferencerCommonFields ref={this.InferencerCommonFields_Ids} {...this.props} info_entity="router" />
                                            ),
                                        },
                                    ]
                                }
                            },
                            getChildrenAfter: (id, _row) => id
                                ? <>
                                    <NSSpace size={NSSpaceSizeType.SMALL} />
                                    <InferencerLoader
                                        {...this.props}
                                        inferencer_type={InferencerType.ROUTER}
                                        inferencer_id={id}
                                        render={({ tree, loading, error }) => (
                                            <>
                                                <InferencerGraph tree={tree} loading={loading} error={error} />
                                                <NSSpace size={NSSpaceSizeType.SMALL} />
                                                <InferencerVisualizer tree={tree} loading={loading} error={error} />
                                            </>
                                        )}
                                    />
                                </>
                                : null,
                        }
                    }
                }}
            >
                <NSColumn>
                    <NSRow>
                        <NSBoxString
                            ref={this.NSBoxString_Name}
                            required
                            title="Name"
                            style={{ width: NSBox.width.double }}
                            info={{ link: "https://namirasoft.com/inference/console-guide/router/#field-name" }}
                        />
                    </NSRow>
                    <NSRow>
                        <InferencerTypeID<RouterDefaultInferencerType>
                            ref={this.InferencerTypeID_Default}
                            {...this.props}
                            typeTitle="Default Inferencer Type"
                            idTitle="Default Inferencer ID"
                            required
                            getEnumObject={() => RouterDefaultInferencerType}
                            defaultType={RouterDefaultInferencerType.MODEL}
                            info={{ link: "https://namirasoft.com/inference/console-guide/router/#field-default-inferencer" }}
                        />
                    </NSRow>
                    <NSBoxTextArea
                        ref={this.NSBoxTextArea_Description}
                        title="Description"
                        required={false}
                        style={{ width: NSBox.width.double }}
                        info={{ link: "https://namirasoft.com/inference/console-guide/router/#field-description" }}
                    />
                </NSColumn>
            </InferenceLayout>
        );
    }
}